import env from 'env'

import {
  REGISTRATION_SUCCESSFUL,
  SUBSCRIPTION_SUCCESSFUL,
} from 'client/shared/reducers/analytics-reducer'
import { getFacebookClickIds } from 'shared/tools/meta-conversion-params'
import { hashEmailSha256, hashPhoneSha256 } from 'shared/tools/crypto-helper'

type FacebookEventState = {
  currentUser?: {
    data?: {
      email?: string | null
      phone_number?: string | null
    }
  }
}

/** Exported for unit tests — used by analytics middleware. */
export function sendFacebookEvent(
  type: string,
  payload: Record<string, unknown>,
  state: FacebookEventState,
): void {
  if (env.isDevelopment() || typeof window.fbq !== 'function') {
    return
  }

  try {
    if (type === REGISTRATION_SUCCESSFUL) {
      const { email, registration_completed_at } = payload
      const { fbp, fbc } = getFacebookClickIds()
      window.fbq('track', 'CompleteRegistration', {
        ...(email != null && email !== '' && { email: String(email) }),
        ...(registration_completed_at != null && {
          registration_completed_at: String(registration_completed_at),
        }),
        ...(fbp && { fbp }),
        ...(fbc && { fbc }),
      })
      return
    }

    if (type === SUBSCRIPTION_SUCCESSFUL) {
      if (payload.payment_method === 'code') {
        const eventId = crypto.randomUUID()
        const { fbp, fbc } = getFacebookClickIds()
        const email = state?.currentUser?.data?.email
        const phone = state?.currentUser?.data?.phone_number
        const basePayload = {
          content_type: payload.sub_type || 'code',
          subscription_completed_at: new Date().toISOString(),
          event_id: eventId,
          ...(fbp && { fbp }),
          ...(fbc && { fbc }),
        }
        Promise.all([
          email != null && String(email).trim() !== ''
            ? hashEmailSha256(String(email))
            : Promise.resolve(null),
          phone != null && String(phone).trim() !== ''
            ? hashPhoneSha256(String(phone))
            : Promise.resolve(null),
        ]).then(([hashedEmail, hashedPhone]) => {
          window.fbq('trackCustom', 'PromoCodeActivated', {
            ...basePayload,
            ...(hashedEmail && { email: hashedEmail }),
            ...(hashedPhone && { phone: hashedPhone }),
          })
        })
        return
      }
      const {
        currency,
        price,
        payment_method,
        is_trial,
        purchase_type,
      } = payload

      // Pixel только в паре с CAPI: без meta_capi_event_id с бэка — ничего не шлём.
      const capiEventId =
        typeof payload.meta_capi_event_id === 'string' &&
        payload.meta_capi_event_id.length > 0
          ? payload.meta_capi_event_id
          : undefined
      if (!capiEventId) {
        return
      }

      if (
        typeof price !== 'number' ||
        typeof currency !== 'string' ||
        !currency
      ) {
        return
      }

      // purchase_type с бэка — источник истины для CAPI-дедупа; иначе fallback на is_trial.
      const isTrialPurchase =
        purchase_type === 'trial' ||
        (purchase_type !== 'regular' && Boolean(is_trial))

      let { sub_type } = payload
      if (isTrialPurchase) {
        sub_type = `${sub_type}_trial`
      }
      if (payment_method === 'trial_3_months') {
        sub_type = `${sub_type}_3_months` // this will result in e.g. premium_trial_3_months
      }

      const { fbp, fbc } = getFacebookClickIds()
      const email = state?.currentUser?.data?.email
      const eventOptions = { eventID: capiEventId }

      if (isTrialPurchase) {
        const baseTrialPayload = {
          currency,
          value: price,
          content_type: sub_type,
          subscription_completed_at: new Date().toISOString(),
          ...(fbp && { fbp }),
          ...(fbc && { fbc }),
        }
        if (email != null && String(email).trim() !== '') {
          hashEmailSha256(String(email)).then(hashed => {
            window.fbq(
              'track',
              'StartTrial',
              {
                ...baseTrialPayload,
                email: hashed,
              },
              eventOptions,
            )
          })
        } else {
          window.fbq('track', 'StartTrial', baseTrialPayload, eventOptions)
        }
        return
      }

      window.fbq(
        'track',
        'Purchase',
        {
          currency,
          value: price,
          content_type: sub_type,
          ...(fbp && { fbp }),
          ...(fbc && { fbc }),
        },
        eventOptions,
      )
    }
  } catch {
    // Ошибки стороннего скрипта Meta не должны прерывать флоу приложения
  }
}
